<script setup>
/**
 * "Claude suggested N changes · Review · Undo all", one line per Claude session that still has
 * something (services spec §8 P-8.4). N counts what waits for her and what was drafted switched
 * off; Review opens each proposal where she decides; Undo all reverses every write of the session
 * (C2's batch undo) and closes what still waits in it. Nothing shows when Claude suggested nothing.
 */
import { computed, onMounted, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import ClaudeSuggestionReview from './ClaudeSuggestionReview.vue';
import SuggestionReview from './SuggestionReview.vue';
import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { batchChangeCount, suggestionsOfBatch } from '../helpers/claude.js';

const props = defineProps({
  accountId: { type: Number, default: null },
});

const { t } = useWasloI18n();

const summary = ref(null);
const open = ref([]);
const reviewing = ref(null);
const busy = ref(false);
const notice = ref('');

const load = async () => {
  if (props.accountId === null) return;
  try {
    const [s, items] = await Promise.all([
      wasloApi.getAssistantSummary(props.accountId),
      wasloApi.getSetupSuggestions(props.accountId, 'claude'),
    ]);
    summary.value = s;
    open.value = items;
  } catch {
    // A secondary read: Overview says nothing about Claude rather than something untrue.
    summary.value = null;
  }
};

onMounted(load);

const batches = computed(() => summary.value?.batches ?? []);

const linkTo = path =>
  props.accountId === null
    ? null
    : frontendURL(`accounts/${props.accountId}/waslo/${path}`);

const toggle = batchId => {
  reviewing.value = reviewing.value === batchId ? null : batchId;
};

const undoAll = async batchId => {
  if (busy.value || props.accountId === null) return;
  busy.value = true;
  notice.value = '';
  try {
    await wasloApi.undoAssistantBatch(props.accountId, batchId);
    notice.value = t('WASLO.CLAUDE.BATCHES.UNDONE');
    reviewing.value = null;
    await load();
  } catch {
    notice.value = t('WASLO.CLAUDE.BATCHES.FAILED');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <section
    v-if="batches.length || notice"
    class="flex flex-col gap-3"
    data-waslo-claude-batches
  >
    <div
      v-for="batch in batches"
      :key="batch.batchId"
      class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2"
      :data-waslo-claude-batch="batch.batchId"
    >
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="size-4 i-lucide-sparkles text-n-blue-10"
          aria-hidden="true"
        />
        <p
          class="mb-0 text-sm font-medium grow text-n-slate-12"
          data-waslo-claude-batch-line
        >
          {{
            t('WASLO.CLAUDE.BATCHES.LINE', { count: batchChangeCount(batch) })
          }}
        </p>
        <Button
          :label="
            reviewing === batch.batchId
              ? t('WASLO.CLAUDE.BATCHES.HIDE')
              : t('WASLO.CLAUDE.BATCHES.REVIEW')
          "
          size="sm"
          variant="faded"
          color="blue"
          data-waslo-claude-batch-review
          @click="toggle(batch.batchId)"
        />
        <Button
          :label="t('WASLO.CLAUDE.BATCHES.UNDO_ALL')"
          size="sm"
          variant="faded"
          color="slate"
          :disabled="busy"
          data-waslo-claude-batch-undo
          @click="undoAll(batch.batchId)"
        />
      </div>

      <div v-if="reviewing === batch.batchId" class="flex flex-col gap-4">
        <template
          v-for="s in suggestionsOfBatch(open, batch.batchId)"
          :key="s.id"
        >
          <SuggestionReview
            v-if="s.kind === 'change_set'"
            :account-id="accountId"
            :suggestion="s"
            data-waslo-claude-review
            @dismissed="load"
          />
          <ClaudeSuggestionReview
            v-else
            :account-id="accountId"
            :suggestion="s"
            @changed="load"
          />
        </template>
        <div
          v-if="batch.drafted > 0"
          class="flex flex-wrap items-center gap-2 text-sm text-n-slate-11"
          data-waslo-claude-batch-drafted
        >
          <span>{{ t('WASLO.CLAUDE.BATCHES.DRAFTED') }}</span>
          <router-link :to="linkTo('tasks')" class="underline">
            {{ t('WASLO.CLAUDE.BATCHES.OPEN_TASKS') }}
          </router-link>
          <router-link :to="linkTo('settings/connections')" class="underline">
            {{ t('WASLO.CLAUDE.BATCHES.OPEN_CONNECTIONS') }}
          </router-link>
        </div>
      </div>
    </div>

    <p
      v-if="notice"
      class="mb-0 text-sm text-n-slate-12"
      data-waslo-claude-batches-notice
    >
      {{ notice }}
    </p>
  </section>
</template>
